import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, ExternalLink } from 'lucide-react';
import api from '../../services/api';
import { Card, PageHeader } from '../../components/ui';

const apiBase = String(api.defaults.baseURL ?? '').replace(/\/v1\/?$/, '');

function Code({ children }: { children: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // presse-papiers indisponible : le texte reste selectionnable
    }
  };
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-lg bg-slate-900 p-4 pr-20 text-xs leading-relaxed text-slate-100"><code>{children}</code></pre>
      <button onClick={copy} className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-slate-700 px-2 py-1 text-xs text-slate-100 hover:bg-slate-600">
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        {copied ? 'Copié' : 'Copier'}
      </button>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <li className="flex gap-4">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-semibold text-white">{n}</span>
      <div className="min-w-0 flex-1 space-y-3">
        <h3 className="font-semibold text-slate-900">{title}</h3>
        {children}
      </div>
    </li>
  );
}

export default function IntegrationPage() {
  return (
    <div>
      <PageHeader title="Intégration" subtitle="De votre première clé au premier document signé, étape par étape" />

      <Card>
        <ol className="space-y-8">
          <Step n={1} title="Récupérez votre clé d'API">
            <p className="text-sm text-slate-600">
              Créez-la dans <Link to="/keys" className="text-indigo-600 hover:underline">Clés d&apos;API</Link> et conservez-la dans un gestionnaire de mots de passe : elle ne sera plus affichée. Gardez-la côté serveur, jamais dans une page web ou une application mobile.
              Chaque appel l&apos;envoie dans l&apos;en-tête <code className="rounded bg-slate-100 px-1">Authorization: Bearer &lt;clé&gt;</code>.
            </p>
            <p className="text-sm text-slate-600">Adresse de l&apos;API : <code className="rounded bg-slate-100 px-1">{apiBase}</code></p>
          </Step>

          <Step n={2} title="Créez un document et ses signataires">
            <p className="text-sm text-slate-600">Le PDF est envoyé en base64 (25 Mo maximum). Un signataire = une signature décomptée de votre solde, une fois terminée.</p>
            <Code>{`API=${apiBase}
KEY=sk_live_...   # votre clé

curl -s $API/v1/documents \\
  -H "Authorization: Bearer $KEY" -H "Content-Type: application/json" \\
  -d "{
    \\"title\\": \\"Contrat de prestation\\",
    \\"external_ref\\": \\"CMD-2026-0042\\",
    \\"file_content\\": \\"$(base64 -w0 contrat.pdf)\\",
    \\"signers\\": [
      {\\"full_name\\": \\"Alice Martin\\", \\"email\\": \\"alice@exemple.fr\\", \\"otp_channel\\": \\"email\\", \\"order_index\\": 0}
    ]
  }"
# → {"id":"<document>","status":"draft"}`}</Code>
          </Step>

          <Step n={3} title="Envoyez-le à la signature">
            <Code>{`curl -s -X POST $API/v1/documents/<document>/send \\
  -H "Authorization: Bearer $KEY" -H "Content-Type: application/json" -d '{}'`}</Code>
            <p className="text-sm text-slate-600">
              Chaque signataire reçoit un e-mail avec son lien. Pour tester, mettez votre propre adresse : vous recevez le lien, vous signez, et vous voyez le résultat dans <Link to="/documents" className="text-indigo-600 hover:underline">Documents</Link>.
            </p>
          </Step>

          <Step n={4} title="Récupérez le document signé et ses preuves">
            <p className="text-sm text-slate-600">Dès que tous ont signé, le document est scellé (signature PAdES horodatée, archivage 5 ans). Téléchargez le PDF et le dossier de preuve depuis <Link to="/documents" className="text-indigo-600 hover:underline">Documents</Link>, ou par l&apos;API :</p>
            <Code>{`curl -s "$API/v1/documents/<document>/download?format=pdf" -H "Authorization: Bearer $KEY"
curl -s  $API/v1/documents/<document>/evidence               -H "Authorization: Bearer $KEY"
# → {"download_url": "...", "expires_in": 900}`}</Code>
          </Step>

          <Step n={5} title="Soyez prévenu automatiquement (webhooks)">
            <p className="text-sm text-slate-600">Plutôt que d&apos;interroger l&apos;API, enregistrez une adresse HTTPS : nous l&apos;appelons à chaque événement (signature, refus, document scellé). Chaque envoi est signé avec un secret affiché une seule fois.</p>
            <Code>{`curl -s $API/v1/webhooks \\
  -H "Authorization: Bearer $KEY" -H "Content-Type: application/json" \\
  -d '{"url": "https://monsite.fr/hooks/signature",
       "events": ["signer.signed", "document.completed", "document.declined"]}'`}</Code>
          </Step>
        </ol>
      </Card>

      <Card title="Aller plus loin" className="mt-6">
        <ul className="space-y-2 text-sm">
          <li>
            <a className="inline-flex items-center gap-1.5 text-indigo-600 hover:underline" href={`${apiBase}/docs`} target="_blank" rel="noreferrer">
              Documentation complète de l&apos;API (essai interactif) <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </li>
          <li className="text-slate-600">Signature visible (tracée ou tapée), page de signatures, marqueurs de position : décrits dans la documentation de l&apos;API.</li>
          <li className="text-slate-600">Si votre solde est insuffisant, l&apos;API répond <code className="rounded bg-slate-100 px-1">402 insufficient_credits</code> et rien n&apos;est envoyé : rechargez dans <Link to="/billing" className="text-indigo-600 hover:underline">Facturation</Link>.</li>
        </ul>
      </Card>
    </div>
  );
}
